import { useCallback, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { getCategoryTree, type CategoryRow, type SectionRow } from "@/lib/categories.functions";
import { getAllCategoryPages, type CategoryPage } from "@/lib/category-pages.functions";
import { listExperiences, type Experience } from "@/lib/experiences.functions";
import CategoryJourney, { type JourneyCategory } from "@/components/CategoryJourney";

const NAV_TARGET_KEY = "backland:navTarget";

const CHOOSER_CATEGORY: JourneyCategory = {
  id: "__path",
  label: "Path",
  short: "Path",
  kind: "chooser",
  slug: null,
  category: null,
  sections: [],
  page: null,
};





function buildCategories(
  cats: CategoryRow[],
  sectionsAll: SectionRow[],
  visibility: { category_id: string; experience_id: string }[],
  pages: CategoryPage[],
  experiences: Experience[],
  selectedSlug: string | null,
): JourneyCategory[] {
  const expBySlug = new Map(experiences.map((e) => [e.slug, e.id]));
  const selectedExpId = selectedSlug ? expBySlug.get(selectedSlug) ?? null : null;
  const visByCat = new Map<string, string[]>();
  for (const v of visibility) {
    const arr = visByCat.get(v.category_id) ?? [];
    arr.push(v.experience_id);
    visByCat.set(v.category_id, arr);
  }
  const secsByCat = new Map<string, SectionRow[]>();
  for (const s of sectionsAll) {
    const arr = secsByCat.get(s.category_id) ?? [];
    arr.push(s);
    secsByCat.set(s.category_id, arr);
  }
  const pagesById = new Map(pages.map((p) => [p.category_id, p]));

  const out: JourneyCategory[] = [];
  for (const c of cats) {
    if (selectedExpId) {
      const vis = visByCat.get(c.id);
      if (vis && vis.length > 0 && !vis.includes(selectedExpId)) continue;
    }
    out.push({
      id: c.id,
      label: c.label,
      short: c.short,
      kind: c.kind,
      slug: c.slug,
      category: c,
      sections: secsByCat.get(c.id) ?? [],
      page: c.kind === "page" ? pagesById.get(c.id) ?? null : null,
    });
  }
  return out;
}

function peekNavTarget(categories: JourneyCategory[]): number | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.sessionStorage.getItem(NAV_TARGET_KEY);
    if (!raw) return null;
    const t = JSON.parse(raw) as { c?: number };
    if (typeof t.c !== "number") return null;
    if (!categories[t.c]) return null;
    window.sessionStorage.removeItem(NAV_TARGET_KEY);
    return t.c;
  } catch {
    return null;
  }
}

export default function StoryExperience({
  initialCategorySlug,
  initialThingsTab,
  chooserError,
}: { initialCategorySlug?: string; initialThingsTab?: "onsite" | "nearby"; chooserError?: string } = {}) {
  const fetchTree = useServerFn(getCategoryTree);
  const fetchPages = useServerFn(getAllCategoryPages);
  const fetchExps = useServerFn(listExperiences);
  const { data: tree } = useQuery({
    queryKey: ["category-tree"],
    queryFn: () => fetchTree(),
    staleTime: 60_000,
  });
  const { data: pages } = useQuery({
    queryKey: ["category-pages"],
    queryFn: () => fetchPages(),
    staleTime: 60_000,
  });
  const { data: experiences } = useQuery({
    queryKey: ["experiences"],
    queryFn: () => fetchExps(),
    staleTime: 60_000,
  });

  if (!tree || !pages || !experiences) {
    return <div className="fixed inset-0 bg-background" />;
  }
  const resolvedTab: "onsite" | "nearby" | undefined =
    initialThingsTab ??
    (initialCategorySlug === "onsite" || initialCategorySlug === "nearby"
      ? initialCategorySlug
      : undefined);
  const resolvedSlug =
    initialCategorySlug === "onsite" || initialCategorySlug === "nearby"
      ? "things-to-do"
      : initialCategorySlug;
  return (
    <StoryInner
      tree={tree}
      pages={pages}
      experiences={experiences}
      initialCategorySlug={resolvedSlug}
      initialThingsTab={resolvedTab}
      chooserError={chooserError}
    />
  );
}

function StoryInner({
  tree,
  pages,
  experiences,
  initialCategorySlug,
  initialThingsTab,
  chooserError,
}: {
  tree: { categories: CategoryRow[]; sections: SectionRow[]; visibility: { category_id: string; experience_id: string }[] };
  pages: CategoryPage[];
  experiences: Experience[];
  initialCategorySlug?: string;
  initialThingsTab?: "onsite" | "nearby";
  chooserError?: string;
}) {
  const pickerExperiences = useMemo(
    () => experiences.filter((e) => e.include_in_picker),
    [experiences],
  );
  const validSlugs = useMemo(
    () => new Set(pickerExperiences.map((e) => e.slug)),
    [pickerExperiences],
  );
  const initialSelectedPath = useMemo<string | null>(() => {
    if (typeof window === "undefined") return null;
    try {
      const v = localStorage.getItem("backland.experience");
      return v && validSlugs.has(v) ? v : null;
    } catch {
      return null;
    }
  }, [validSlugs]);
  const [selectedPath, setSelectedPath] = useState<string | null>(initialSelectedPath);

  const categories = useMemo<JourneyCategory[]>(() => {
    const base = buildCategories(
      tree.categories,
      tree.sections,
      tree.visibility,
      pages,
      experiences,
      selectedPath,
    );
    if (base.length === 0) return [CHOOSER_CATEGORY];
    return [CHOOSER_CATEGORY, ...base];
  }, [tree, pages, experiences, selectedPath]);

  const firstContentIdx = useMemo(
    () => categories.findIndex((c) => c.kind !== "chooser"),
    [categories],
  );
  const finalSectionsIdx = useMemo(() => {
    for (let i = categories.length - 1; i >= 0; i--) {
      if (categories[i].kind === "sections") return i;
    }
    return -1;
  }, [categories]);

  const targetCategoryIndex = useMemo(() => {
    if (!initialCategorySlug) return -1;
    return categories.findIndex(
      (c) => c.slug === initialCategorySlug || c.id === initialCategorySlug,
    );
  }, [categories, initialCategorySlug]);

  const initialIndex = useMemo(() => {
    const navTarget = peekNavTarget(categories);
    if (navTarget != null) return navTarget;
    if (chooserError) return 0;
    // If the user already picked a path, jump straight to the target (or first content).
    if (initialSelectedPath) {
      if (targetCategoryIndex >= 0) return targetCategoryIndex;
      if (firstContentIdx >= 0) return firstContentIdx;
    }
    // Fresh visitor: always start on the chooser so the path gets picked first.
    return 0;
  }, [categories, targetCategoryIndex, initialSelectedPath, chooserError, firstContentIdx]);


  const handlePickPath = useCallback((slug: string) => {
    try {
      localStorage.setItem("backland.experience", slug);
    } catch {
      /* noop */
    }
    setSelectedPath(slug);
  }, []);

  return (
    <CategoryJourney
      categories={categories}
      initialIndex={initialIndex}
      selectedPath={selectedPath}
      chooserError={chooserError}
      onPickPath={handlePickPath}
      finalStoryIndex={finalSectionsIdx}
      postChooserTargetIndex={targetCategoryIndex}
      initialThingsTab={initialThingsTab}
    />
  );
}
